import type {
  CurrencyCode,
  InvoiceItemInput,
  InvoiceTotals,
  RateUnitPreset,
} from "./types";

const CURRENCY_LOCALE = "en-US";

export const CURRENCY_LABELS: Record<CurrencyCode, string> = {
  USD: "US Dollar",
  PHP: "Philippine Peso",
  AUD: "Australian Dollar",
  CAD: "Canadian Dollar",
  EUR: "Euro",
  GBP: "British Pound",
  SGD: "Singapore Dollar",
};

export const RATE_UNIT_LABELS: Record<RateUnitPreset, string> = {
  hour: "hour",
  day: "day",
  week: "week",
  month: "month",
  project: "project",
  item: "item",
  page: "page",
  post: "post",
  funnel: "funnel",
  automation: "automation",
  training_batch: "training batch",
  custom: "custom",
};

export const HOURLY_RATE_UNITS: RateUnitPreset[] = ["hour"];
export const FIXED_RATE_UNITS: RateUnitPreset[] = [
  "project",
  "month",
  "week",
  "day",
  "funnel",
  "automation",
  "training_batch",
  "custom",
];
export const QUANTITY_RATE_UNITS: RateUnitPreset[] = [
  "item",
  "page",
  "post",
  "funnel",
  "automation",
  "training_batch",
  "custom",
];

export function formatCurrency(
  amountCents: number,
  currency: CurrencyCode,
): string {
  return new Intl.NumberFormat(CURRENCY_LOCALE, {
    style: "currency",
    currency,
    currencyDisplay: "symbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((Number.isFinite(amountCents) ? amountCents : 0) / 100);
}

export function currencyOptionLabel(currency: CurrencyCode): string {
  return `${currency} — ${CURRENCY_LABELS[currency]}`;
}

export function decimalToCents(value: number | string): number {
  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/,/g, "").trim());

  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }

  return Math.round(numeric * 100);
}

export function centsToDecimal(cents: number): number {
  return Math.round(cents) / 100;
}

export function decimalToMilli(value: number | string): number {
  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/,/g, "").trim());

  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }

  return Math.round(numeric * 1000);
}

export function milliToDecimal(value: number): number {
  return Math.round(value) / 1000;
}

export function parseDuration(value: string): number {
  const trimmed = value.trim();

  if (!trimmed) {
    return 0;
  }

  const parts = trimmed.split(":");

  if (parts.length < 2 || parts.length > 3) {
    return Number.NaN;
  }

  const [hoursText, minutesText, secondsText = "0"] = parts;
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  const seconds = Number(secondsText);

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    !Number.isInteger(seconds) ||
    hours < 0 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 59
  ) {
    return Number.NaN;
  }

  return hours * 3600 + minutes * 60 + seconds;
}

export function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;

  return `${hours}:${String(minutes).padStart(2, "0")}:${String(
    seconds,
  ).padStart(2, "0")}`;
}

export function calculateItemAmount(item: InvoiceItemInput): number {
  switch (item.billingType) {
    case "hourly":
      return Math.round(
        (Math.max(0, item.durationSeconds) *
          Math.max(0, item.hourlyRateCents)) /
          3600,
      );

    case "quantity":
      return Math.round(
        (Math.max(0, item.quantityMilli) *
          Math.max(0, item.unitPriceCents)) /
          1000,
      );

    case "fixed":
      return Math.max(0, Math.round(item.fixedAmountCents));

    default: {
      const exhaustive: never = item.billingType;
      return exhaustive;
    }
  }
}

export function calculateTotals(
  items: InvoiceItemInput[],
  taxRateBps: number,
  amountPaidCents = 0,
): InvoiceTotals {
  const subtotalCents = items.reduce(
    (sum, item) => sum + calculateItemAmount(item),
    0,
  );

  const safeTaxRate = Math.min(
    10_000,
    Math.max(0, Math.round(taxRateBps)),
  );

  const taxCents = Math.round(
    (subtotalCents * safeTaxRate) / 10_000,
  );

  const totalCents = subtotalCents + taxCents;
  const paid = Math.min(
    totalCents,
    Math.max(0, Math.round(amountPaidCents)),
  );

  return {
    subtotalCents,
    taxCents,
    totalCents,
    amountPaidCents: paid,
    balanceCents: Math.max(0, totalCents - paid),
  };
}

export function billingTypeLabel(
  type: InvoiceItemInput["billingType"],
): string {
  if (type === "hourly") {
    return "Hourly";
  }

  if (type === "quantity") {
    return "Quantity";
  }

  return "Fixed amount";
}

export function normalizeUnitLabel(
  unitLabel: string,
  fallback: string,
): string {
  const normalized = unitLabel.trim().replace(/\s+/g, " ");
  return normalized || fallback;
}

export function unitLabelForItem(item: InvoiceItemInput): string {
  if (item.billingType === "hourly") {
    return "hour";
  }

  if (item.billingType === "fixed") {
    return normalizeUnitLabel(item.unitLabel, "project");
  }

  return normalizeUnitLabel(item.unitLabel, "item");
}

export function formatRate(
  amountCents: number,
  currency: CurrencyCode,
  unitLabel: string,
): string {
  return `${formatCurrency(amountCents, currency)} / ${normalizeUnitLabel(
    unitLabel,
    "unit",
  )}`;
}

export function formatItemPricingDetail(
  item: InvoiceItemInput,
  currency: CurrencyCode,
): string {
  const unitLabel = unitLabelForItem(item);

  if (item.billingType === "hourly") {
    return `${formatDuration(item.durationSeconds)} × ${formatRate(
      item.hourlyRateCents,
      currency,
      unitLabel,
    )}`;
  }

  if (item.billingType === "quantity") {
    return `${milliToDecimal(item.quantityMilli)} ${unitLabel} × ${formatRate(
      item.unitPriceCents,
      currency,
      unitLabel,
    )}`;
  }

  return `${formatRate(
    item.fixedAmountCents,
    currency,
    unitLabel,
  )} fixed`;
}
